import React, { useState, useEffect } from 'react';
import { 
  Award, IndianRupee, Calendar, ShieldCheck, CheckCircle, 
  Phone, MapPin, Truck, RefreshCw, Users, Clock, Star,
  Droplets, Building2, Factory, Home, ChevronLeft, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for "suitable for" indicator on each rental card
const SUITABLE_ICON = {
  home: Home,
  building: Building2,
  factory: Factory,
  droplets: Droplets,
  users: Users,
};

// Accent color presets — accent_color (hex) -> visual classes the cards use
const ACCENT_PRESETS = {
  '#0ea5e9': { borderColor: 'border-sky-200',    hoverBorder: 'hover:border-sky-400',    badgeBg: 'bg-sky-600',    bgGradient: 'from-sky-600 to-sky-700' },
  '#0891b2': { borderColor: 'border-cyan-200',   hoverBorder: 'hover:border-cyan-400',   badgeBg: 'bg-cyan-600',   bgGradient: 'from-cyan-600 to-cyan-700' },
  '#16a34a': { borderColor: 'border-green-200',  hoverBorder: 'hover:border-green-400',  badgeBg: 'bg-green-600',  bgGradient: 'from-green-600 to-emerald-600' },
  '#2563eb': { borderColor: 'border-blue-200',   hoverBorder: 'hover:border-blue-400',   badgeBg: 'bg-blue-600',   bgGradient: 'from-blue-600 to-blue-700' },
  '#7c3aed': { borderColor: 'border-violet-200', hoverBorder: 'hover:border-violet-400', badgeBg: 'bg-violet-600', bgGradient: 'from-violet-600 to-purple-700' },
};
const defaultAccent = { borderColor: 'border-blue-200', hoverBorder: 'hover:border-blue-400', badgeBg: 'bg-blue-600', bgGradient: 'from-blue-600 to-green-600' };

// Multi-image carousel for each rental model card
const ModelImages = ({ images = [], name = '' }) => {
  const [idx, setIdx] = useState(0);
  const safe = (Array.isArray(images) ? images : []).filter(Boolean);
  if (safe.length === 0) {
    return (
      <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-lg h-48 flex items-center justify-center" data-testid="model-image-placeholder">
        <Droplets className="text-blue-300" size={64} />
      </div>
    );
  }
  const next = () => setIdx((i) => (i + 1) % safe.length);
  const prev = () => setIdx((i) => (i - 1 + safe.length) % safe.length);
  return (
    <div className="relative bg-gradient-to-br from-gray-50 to-white rounded-lg overflow-hidden">
      <img src={safe[idx]} alt={`${name} ${idx + 1}`} className="w-full h-48 object-contain" data-testid="model-image-active" />
      {safe.length > 1 && (
        <>
          <button type="button" onClick={prev} aria-label="Previous image"
            className="absolute left-1 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-1 shadow"
            data-testid="model-image-prev">
            <ChevronLeft size={16} />
          </button>
          <button type="button" onClick={next} aria-label="Next image"
            className="absolute right-1 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-1 shadow"
            data-testid="model-image-next">
            <ChevronRight size={16} />
          </button>
          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-1">
            {safe.map((_, i) => (
              <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === idx ? 'bg-blue-600' : 'bg-gray-300'}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// CMS reason/badge icon names -> lucide components
const CMS_ICONS = { IndianRupee, ShieldCheck, RefreshCw, Clock, CheckCircle, Award, Calendar, Truck, Users, Star, Droplets, Home, Building2, Factory, MapPin, Phone };

const DEFAULT_CMS = {
  hero: {
    title: 'RO Water Purifier On Rent in Jaipur',
    subtitle: 'Home & Commercial RO Systems starting at just',
    badge_text: 'Pure Water. Zero Tension.',
    image: '/logo.png',
    cta_primary: 'Book Now - Get Instant Callback',
    cta_secondary: 'Call: 9887068888',
    phone: '+919887068888',
    trust_badges: [{ text: 'Free Installation' }, { text: '₹0 Maintenance' }, { text: 'Free Filter Replacement' }],
  },
  why_rent: {
    title: 'Why Rent RO from RO Dekho?',
    reasons: [
      { icon: 'IndianRupee', title: '₹0 Upfront Cost', description: 'No need to invest ₹12,000-₹22,000 upfront' },
      { icon: 'ShieldCheck', title: 'Free Maintenance', description: 'All services & filter changes included' },
      { icon: 'RefreshCw', title: 'Buy-Back Option', description: 'Purchase the RO after 6 months' },
      { icon: 'Clock', title: '24/7 Support', description: 'Same-day service across Jaipur' },
    ],
  },
  plans_heading: { title: 'Choose Your Rental Plan', subtitle: 'Home & Commercial RO Systems by Willfread' },
  booking_heading: { title: 'Book Your RO on Rent', subtitle: 'Fill the form below and get instant callback within 30 minutes' },
  comparison_heading: { title: 'Compare All Plans' },
  faq: { title: 'Frequently Asked Questions', items: [] },
  testimonials: { title: 'Happy Rental Customers', testimonials: [] },
  cta: { title: 'Ready to Get Pure Water with Zero Investment?', subtitle: 'Home or Commercial — we have the perfect RO rental plan for you!', cta_text: 'Book Now', cta_link: '#booking-form' },
};

const ROOnRentPage = () => {
  const [rentModels, setRentModels] = useState([]);
  const [cms, setCms] = useState(DEFAULT_CMS);
  const [hidden, setHidden] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    area: '',
    model: '',
    familySize: '4'
  });

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/rent-models`)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        // Decorate each model with computed UI fields
        const decorated = (Array.isArray(data) ? data : []).map(m => ({
          ...m,
          // Backwards-compat: if no images array but has single `image`, wrap it
          images: m.images?.length > 0 ? m.images : (m.image ? [m.image] : []),
          suitableIcon: SUITABLE_ICON[m.suitable_icon] || (m.category === 'commercial' ? Building2 : Home),
          ...(ACCENT_PRESETS[m.accent_color] || defaultAccent),
        }));
        setRentModels(decorated);
        if (decorated.length > 0 && !formData.model) {
          setFormData(f => ({ ...f, model: decorated[0].slug || decorated[0].id }));
        }
      });

    fetch(`${BACKEND_URL}/api/cms/sections/ro-on-rent`)
      .then(r => r.ok ? r.json() : [])
      .then(sections => {
        if (!Array.isArray(sections) || sections.length === 0) return;
        const next = { ...DEFAULT_CMS };
        const off = {};
        sections.forEach(s => {
          const key = s.key || s.section_type;
          if (s.is_visible === false) off[key] = true;
          next[key] = { ...(DEFAULT_CMS[key] || {}), ...s };
        });
        setCms(next);
        setHidden(off);
      })
      .catch(() => {});
  }, []);

  const minPrice = rentModels.length ? Math.min(...rentModels.map(m => Number(m.price) || Infinity)) : 350;
  const heroPhone = cms.hero.phone || '+919887068888';
  const scrollToForm = () => document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const selected = rentModels.find(m => (m.slug || m.id) === formData.model);
    try {
      await fetch(`${BACKEND_URL}/api/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          ...(formData.email ? { email: formData.email } : {}),
          serviceType: 'rental',
          message: `RO on Rent: ${selected?.name || formData.model} | Family size: ${formData.familySize}`,
          ...(formData.address ? { address: formData.address } : {}),
          ...(formData.area ? { area: formData.area } : {}),
          city: 'Jaipur',
          source: 'ro_on_rent_page'
        })
      });
      toast.success('Booking Request Received!', {
        description: 'Our team will contact you within 30 minutes to confirm your RO rental.'
      });
      setFormData({
        name: '', phone: '', email: '', address: '', area: '',
        model: rentModels[0]?.slug || rentModels[0]?.id || '',
        familySize: '4'
      });
    } catch {
      toast.error('Could not submit. Please call us directly on +91 9887068888.');
    }
  };


  const jaipurAreas = [
    'Malviya Nagar', 'Vaishali Nagar', 'Mansarovar', 'Jagatpura', 
    'Pratap Nagar', 'Raja Park', 'C-Scheme', 'MI Road',
    'Tonk Road', 'Ajmer Road', 'Sikar Road', 'JLN Marg',
    'Sodala', 'Shipra Path', 'Bani Park', 'Other'
  ];

  const faqs = cms.faq.items || [];
  const testimonials = cms.testimonials.testimonials || [];
  const reasons = cms.why_rent.reasons || [];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="ro-on-rent-page">
      <Header />

      {/* Hero Banner */}
      {!hidden.hero && (
      <div className="bg-gradient-to-r from-blue-700 via-blue-600 to-green-600 text-white py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            {cms.hero.image && (
              <div className="mb-8 inline-block">
                <img 
                  src={cms.hero.image} 
                  alt="RO Dekho - RO on Rent in Jaipur" 
                  className="h-32 md:h-40 w-auto drop-shadow-2xl"
                  data-testid="hero-image"
                />
              </div>
            )}
            
            {cms.hero.badge_text && (
              <Badge className="mb-4 bg-white text-blue-700 hover:bg-gray-100 text-base md:text-lg px-6 py-2" data-testid="hero-badge">
                {cms.hero.badge_text}
              </Badge>
            )}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight" data-testid="hero-title">
              {cms.hero.title}
            </h1>
            <p className="text-lg md:text-2xl text-blue-100 mb-4" data-testid="hero-subtitle">
              {cms.hero.subtitle}
            </p>
            <div className="text-4xl md:text-5xl font-bold mb-8" data-testid="hero-price">
              ₹{minPrice}<span className="text-xl md:text-2xl font-normal">/- Month</span>
            </div>
            <div className="flex flex-wrap gap-3 justify-center mb-8">
              {(cms.hero.trust_badges || []).map((b, i) => (
                <span key={i} className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium" data-testid={`hero-trust-badge-${i}`}>{typeof b === 'string' ? b : b.text}</span>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={scrollToForm}
                size="lg"
                className="bg-white hover:bg-gray-100 text-blue-700 font-bold px-10 py-7 text-lg rounded-lg shadow-xl"
                data-testid="hero-book-now-btn"
              >
                {cms.hero.cta_primary}
              </Button>
              <a 
                href={`tel:${heroPhone}`}
                className="inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold px-10 py-7 text-lg rounded-lg shadow-xl transition-all"
              >
                <Phone size={24} />
                {cms.hero.cta_secondary}
              </a>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Why Rent Section */}
      {!hidden.why_rent && reasons.length > 0 && (
      <div className="py-16 bg-white" data-testid="why-rent-section">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            {cms.why_rent.title}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 max-w-6xl mx-auto">
            {reasons.map((item, i) => {
              const Icon = CMS_ICONS[item.icon] || Star;
              const green = i % 2 === 0;
              return (
              <Card key={i} className={`border-2 ${green ? 'border-green-200 hover:border-green-400' : 'border-blue-200 hover:border-blue-400'} transition-all hover:shadow-xl`}>
                <CardContent className="p-4 md:p-6 text-center">
                  <div className={`${green ? 'bg-green-100' : 'bg-blue-100'} w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4`}>
                    <Icon className={green ? 'text-green-600' : 'text-blue-600'} size={28} />
                  </div>
                  <h3 className="font-bold text-base md:text-xl text-gray-900 mb-1 md:mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-xs md:text-base">{item.description || item.desc}</p>
                </CardContent>
              </Card>
              );
            })}
          </div>
        </div>
      </div>
      )}

      {/* Rent Models */}
      <div className="py-16 bg-gradient-to-b from-gray-50 to-white" data-testid="rent-models-section">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4" data-testid="plans-heading">
            {cms.plans_heading.title}
          </h2>
          <p className="text-center text-gray-600 mb-12 text-lg">{cms.plans_heading.subtitle}</p>
          
          <div className="grid lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {rentModels.map((model) => {
              const SuitableIcon = model.suitableIcon;
              return (
                <Card key={model.id}
                  className={`${model.borderColor} ${model.hoverBorder} border-2 transition-all shadow-lg hover:shadow-2xl overflow-hidden`}
                  data-testid={`model-card-${model.id}`}
                >
                  <CardContent className="p-0">
                    {/* Tag Badge */}
                    <div className={`${model.badgeBg} text-white text-center py-2 text-xs font-bold tracking-wider`}>
                      {model.tag} &mdash; {model.brand}
                    </div>

                    {/* Product Image Carousel */}
                    <div className="relative bg-white p-2" data-testid={`model-image-${model.slug || model.id}`}>
                      <ModelImages images={model.images} name={model.name} />
                    </div>

                    <div className="p-5 md:p-6">
                      {/* Model Name */}
                      <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-1" data-testid={`model-name-${model.id}`}>
                        {model.name}
                      </h3>
                      <p className="text-sm text-gray-500 mb-4 flex items-center gap-1.5">
                        <SuitableIcon size={14} /> {model.suitable}
                      </p>

                      {/* Price */}
                      <div className="bg-red-500 text-white rounded-xl p-4 mb-4 text-center" data-testid={`model-price-${model.id}`}>
                        <div className="text-sm font-medium mb-1">Monthly Rent</div>
                        <div className="text-4xl md:text-5xl font-bold">
                          ₹{model.price}<span className="text-lg font-normal">/-</span>
                        </div>
                      </div>

                      {/* Free Services */}
                      <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
                        <div className="text-center mb-2">
                          <span className="text-2xl font-bold text-green-700">FREE</span>
                        </div>
                        <div className="space-y-1.5">
                          {model.freeServices.map((s, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm text-green-800 font-medium">
                              <CheckCircle size={16} className="text-green-600 flex-shrink-0" /> {s}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Features */}
                      <div className="space-y-2 mb-4">
                        {model.features.map((feature, index) => (
                          <div key={index} className="flex items-start gap-2">
                            <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                            <span className="text-gray-700 text-sm">{feature}</span>
                          </div>
                        ))}
                      </div>

                      {/* Terms */}
                      <div className="bg-gray-50 rounded-xl p-4 space-y-2.5 text-sm mb-5">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Security Deposit:</span>
                          <span className="font-bold text-gray-900">₹{model.deposit.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Lock-in Period:</span>
                          <span className="font-bold text-gray-900">{model.lockIn}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Buy-Back After:</span>
                          <span className="font-bold text-gray-900">{model.buybackAfter}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Buying Value:</span>
                          <span className="font-bold text-green-700">₹{model.buyback.toLocaleString()}</span>
                        </div>
                      </div>

                      <Button 
                        onClick={() => {
                          setFormData({...formData, model: model.slug || model.id});
                          document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' });
                        }}
                        className={`w-full bg-gradient-to-r ${model.bgGradient} text-white py-5 text-base font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all`}
                        data-testid={`book-btn-${model.slug || model.id}`}
                      >
                        Book {model.name} Now
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* Booking Form Section */}
      <div id="booking-form" className="py-16 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-testid="booking-heading">
                {cms.booking_heading.title}
              </h2>
              <p className="text-lg text-gray-600">{cms.booking_heading.subtitle}</p>
            </div>

            <Card className="border-2 border-blue-200 shadow-2xl" data-testid="booking-form-card">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="booking-form">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                      <Input id="name" name="name" type="text" required value={formData.name} onChange={handleChange}
                        placeholder="Enter your full name" className="w-full px-4 py-6 border-2 border-gray-200 focus:border-blue-500 rounded-lg" data-testid="input-name" />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                      <Input id="phone" name="phone" type="tel" required value={formData.phone} onChange={handleChange}
                        placeholder="Enter your phone number" className="w-full px-4 py-6 border-2 border-gray-200 focus:border-blue-500 rounded-lg" data-testid="input-phone" />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
                    <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange}
                      placeholder="Enter your email (optional)" className="w-full px-4 py-6 border-2 border-gray-200 focus:border-blue-500 rounded-lg" data-testid="input-email" />
                  </div>

                  <div>
                    <label htmlFor="area" className="block text-sm font-semibold text-gray-700 mb-2">Your Area in Jaipur *</label>
                    <select id="area" name="area" required value={formData.area} onChange={handleChange}
                      className="w-full px-4 py-4 border-2 border-gray-200 focus:border-blue-500 rounded-lg bg-white" data-testid="select-area">
                      <option value="">Select your area</option>
                      {jaipurAreas.map((area) => (<option key={area} value={area}>{area}</option>))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="address" className="block text-sm font-semibold text-gray-700 mb-2">Complete Address *</label>
                    <Textarea id="address" name="address" required value={formData.address} onChange={handleChange}
                      placeholder="Enter your complete address" rows={3}
                      className="w-full px-4 py-3 border-2 border-gray-200 focus:border-blue-500 rounded-lg resize-none" data-testid="input-address" />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="model" className="block text-sm font-semibold text-gray-700 mb-2">Select Model *</label>
                      <select id="model" name="model" required value={formData.model} onChange={handleChange}
                        className="w-full px-4 py-4 border-2 border-gray-200 focus:border-blue-500 rounded-lg bg-white" data-testid="select-model">
                        {rentModels.map(m => (
                          <option key={m.slug || m.id} value={m.slug || m.id}>
                            {m.name} - ₹{m.price}/month {m.suitable ? `(${m.suitable})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="familySize" className="block text-sm font-semibold text-gray-700 mb-2">Usage Type *</label>
                      <select id="familySize" name="familySize" required value={formData.familySize} onChange={handleChange}
                        className="w-full px-4 py-4 border-2 border-gray-200 focus:border-blue-500 rounded-lg bg-white" data-testid="select-usage">
                        <option value="4">Home (up to 8 adults)</option>
                        <option value="office-small">Small Office / Cafe / Institute</option>
                        <option value="office-large">Large Office / Factory / School</option>
                      </select>
                    </div>
                  </div>

                  <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={20} />
                      <p className="text-sm text-gray-700">
                        By submitting this form, you agree to receive a callback from our team within 30 minutes to confirm your RO rental booking.
                      </p>
                    </div>
                  </div>

                  <Button type="submit" data-testid="submit-booking-btn"
                    className="w-full bg-gradient-to-r from-blue-700 to-green-600 hover:from-blue-800 hover:to-green-700 text-white py-6 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all">
                    Submit Booking Request - Get Instant Callback
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-gray-600 mb-2">Or call us directly:</p>
                  <a href="tel:+919887068888" className="text-2xl font-bold text-blue-700 hover:text-blue-800">
                    <Phone size={20} className="inline mr-2" />9887068888
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Comparison Table — built live from rent models */}
      {!hidden.comparison_heading && rentModels.length > 0 && (
      <div className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12" data-testid="comparison-heading">
            {cms.comparison_heading.title}
          </h2>
          <div className="max-w-6xl mx-auto overflow-x-auto">
            <table className="w-full border-collapse text-sm" data-testid="comparison-table">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left p-4 font-semibold text-gray-700 border-b-2">Feature</th>
                  {rentModels.map(m => (
                    <th key={m.slug || m.id} className="p-4 font-semibold text-blue-700 border-b-2 text-center whitespace-nowrap">{m.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ['Monthly Rent', m => `₹${Number(m.price || 0).toLocaleString('en-IN')}`],
                  ['Best For', m => m.suitable || '-'],
                  ['Technology', m => m.tech || '-'],
                  ['Capacity', m => m.capacity || '-'],
                  ['Security Deposit', m => `₹${Number(m.deposit || 0).toLocaleString('en-IN')}`],
                  ['Lock-in Period', m => m.lockIn || '-'],
                  ['Buy-Back Value', m => `₹${Number(m.buyback || 0).toLocaleString('en-IN')}`],
                  ['Buy-Back After', m => m.buybackAfter || '-'],
                  ['Installation', () => 'Free'],
                  ['Maintenance', () => 'Free (₹0)'],
                  ['Filter Replacement', () => 'Free'],
                ].map(([label, fn], i) => (
                  <tr key={label} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="p-3 md:p-4 font-medium text-gray-800 border-b">{label}</td>
                    {rentModels.map(m => (
                      <td key={m.slug || m.id} className={`p-3 md:p-4 text-center border-b ${i === 0 ? 'font-bold text-lg text-red-600' : 'text-gray-700'}`}>{fn(m)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      )}

      {/* FAQ Section */}
      {!hidden.faq && faqs.length > 0 && (
      <div className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12" data-testid="faq-heading">
              {cms.faq.title}
            </h2>
            
            <Accordion type="single" collapsible className="w-full space-y-3" data-testid="faq-section">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}
                  className="border-2 border-gray-200 rounded-lg px-6 hover:border-blue-300 transition-colors bg-white">
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-blue-700 py-4">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 leading-relaxed pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="mt-12 text-center bg-gradient-to-r from-blue-50 to-green-50 rounded-xl p-8">
              <p className="text-lg text-gray-700 mb-4">Still have questions? We&apos;re here to help!</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:+919887068888"
                  className="inline-flex items-center justify-center gap-2 bg-blue-700 hover:bg-blue-800 text-white font-semibold px-8 py-4 rounded-lg transition-all">
                  <Phone size={20} /> Call: 9887068888
                </a>
                <a href="https://wa.me/919887068888" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-semibold px-8 py-4 rounded-lg transition-all">
                  <Phone size={20} /> WhatsApp Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Testimonials */}
      {!hidden.testimonials && testimonials.length > 0 && (
      <div className="py-16 bg-white" data-testid="rent-testimonials-section">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            {cms.testimonials.title}
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {testimonials.map((t, index) => (
              <Card key={t.id || index} className="border-2 border-green-200 hover:border-green-400 transition-all">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-3">
                    {[...Array(Number(t.rating) || 5)].map((_, i) => (
                      <Star key={i} size={18} className="fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic">&ldquo;{t.content || t.review}&rdquo;</p>
                  <div className="flex items-center gap-3">
                    {t.image ? (
                      <img src={t.image} alt={t.name} className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-green-500 rounded-full flex items-center justify-center text-white font-bold">
                        {(t.name || '?')[0]}
                      </div>
                    )}
                    <div>
                      <div className="font-semibold text-gray-900">{t.name}</div>
                      <div className="text-sm text-gray-600">{t.location || t.area}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
      )}

      {/* Final CTA */}
      {!hidden.cta && (
      <div className="py-16 bg-gradient-to-r from-blue-700 to-green-600 text-white" data-testid="rent-cta-section">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" data-testid="cta-title">
            {cms.cta.title}
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            {cms.cta.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {cms.cta.cta_link && !cms.cta.cta_link.startsWith('#') ? (
              <a href={cms.cta.cta_link} className="inline-flex items-center justify-center bg-white hover:bg-gray-100 text-blue-700 font-bold px-10 py-7 text-lg rounded-lg shadow-xl" data-testid="cta-book-now">
                {cms.cta.cta_text || 'Book Now'}
              </a>
            ) : (
              <Button 
                onClick={scrollToForm}
                size="lg"
                className="bg-white hover:bg-gray-100 text-blue-700 font-bold px-10 py-7 text-lg rounded-lg shadow-xl"
                data-testid="cta-book-now"
              >
                {cms.cta.cta_text || 'Book Now'}
              </Button>
            )}
            <a 
              href={`tel:${heroPhone}`}
              className="inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold px-10 py-7 text-lg rounded-lg shadow-xl transition-all"
            >
              <Phone size={24} />
              {cms.hero.cta_secondary}
            </a>
          </div>
        </div>
      </div>
      )}

      <Footer />
    </div>
  );
};

export default ROOnRentPage;
